import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Pagination, Status, TableData } from '@/components';
import { Column } from '@/types/common';
import {
  ChargeStatus,
  ChargeType,
  CHARGE_STATUS_LABEL,
  CHARGE_TYPE_LABEL,
  EMPTY_CHARGE_SUMMARY,
  RentalCharge,
  RentalChargeSummary,
  RentalWaiver,
} from '@/types/rental';
import { getRentalCharges } from '@/services/api/rentals';
// Straight from the module rather than the `@/utils` barrel: the barrel also
// re-exports the axios client, which drags an ESM-only dependency into every
// Jest suite that reaches this file.
import { extractApiErrorMessage } from '@/utils/errorHandler';
import { formatDateTime, formatRupiah } from '@/utils/format';
import { formatActor, formatDate, waivedDayLabel } from '../rentalDisplay';

/** "Semua" is the absence of a status filter, not a status. */
export type ChargeStatusFilter = 'ALL' | ChargeStatus;

interface ChargeLedgerTabProps {
  rentalId: string;
  /** From the rental detail; names the referral behind a waived day. */
  waivers?: RentalWaiver[];
  /**
   * Controlled by the parent so the detail screen's Tunggakan figure can drop
   * ops straight into the unpaid days — see `RentalDetailPage`.
   */
  statusFilter: ChargeStatusFilter;
  onStatusFilterChange: (status: ChargeStatusFilter) => void;
  /**
   * Satu pembayaran dari tab Riwayat Pembayaran, dipakai menyempitkan ledger
   * ke hari-hari yang ditutupnya. `code` hanya untuk ditampilkan; `ref` yang
   * dikirim ke server.
   */
  collectionFilter?: { ref: string; code: string } | null;
  onClearCollectionFilter?: () => void;
}

/**
 * CANCELLED is deliberately absent: a struck day owes nothing and was never
 * paid, so it is noise in a filter meant to answer "what is still unpaid".
 * It still renders in the table under Semua.
 */
const STATUS_FILTERS: Array<{ key: ChargeStatusFilter; label: string }> = [
  { key: 'ALL', label: 'Semua' },
  ...Object.values(ChargeStatus)
    .filter((status) => status !== ChargeStatus.CANCELLED)
    .map((status) => ({ key: status, label: CHARGE_STATUS_LABEL[status] })),
];

/** The muted line under a cell's headline value. */
const subLine = (children: React.ReactNode, title?: string) => (
  <>
    <br />
    <span className="text-[12px] text-gray-500" title={title}>
      {children}
    </span>
  </>
);

const buildColumns = (waivers?: RentalWaiver[]): Column<RentalCharge>[] => [
  {
    key: 'chargeDate',
    title: 'Tanggal',
    render: (value: string) => (
      <span className="text-[14px] text-gray-900">{formatDate(value)}</span>
    ),
  },
  {
    key: 'createdAt',
    title: 'Dibuat',
    // The audit trail, not the billed day: a nightly-engine row and a day an
    // admin added by hand are otherwise indistinguishable in this table.
    render: (_value: unknown, record: RentalCharge) => (
      <span className="text-[14px] text-gray-900">
        {formatDateTime(record.createdAt)}
        {subLine(formatActor(record.createdBy), record.createdBy || undefined)}
      </span>
    ),
  },
  {
    key: 'type',
    title: 'Jenis',
    render: (value: ChargeType) => (
      <span className="text-[14px] text-gray-900">
        {CHARGE_TYPE_LABEL[value] || value || '-'}
      </span>
    ),
  },
  {
    key: 'amount',
    title: 'Jumlah',
    render: (value: number) => (
      <span className="text-[14px] font-medium text-gray-900">
        {formatRupiah(value, '-', { space: false })}
      </span>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    render: (value: string) =>
      value ? (
        <Status value={value} type="rentalCharge" size="medium" />
      ) : (
        <span className="text-gray-400">-</span>
      ),
  },
  {
    key: 'collection',
    title: 'Dibayar Oleh',
    render: (_value: unknown, record: RentalCharge) => {
      // A waived day is settled without money, so it says so rather than
      // rendering an empty dash that reads like missing data.
      if (record.status === ChargeStatus.WAIVED || record.freeDay) {
        return (
          <span className="text-[14px] text-gray-500">
            {waivedDayLabel(record.chargeDate, waivers)}
          </span>
        );
      }
      if (!record.paidSource) {
        return <span className="text-gray-400">-</span>;
      }

      // A day drained from prepaid balance carries no collection and no payer,
      // so it names the balance rather than reading as paid by nobody.
      const settledBy = record.collection
        ? record.collection.code || record.collection.id
        : 'Saldo prepaid';

      return (
        <span className="text-[14px] text-gray-900">
          {settledBy}
          {record.paidBy && subLine(formatActor(record.paidBy), record.paidBy)}
          {/* Time included: settling a day at 23:58 versus 00:02 is the
              difference between paid and in arrears. */}
          {record.paidAt && subLine(formatDateTime(record.paidAt))}
        </span>
      );
    },
  },
];

/** One figure of the ledger header. */
const SummaryCell: React.FC<{
  label: string;
  amount: number;
  toneClass?: string;
}> = ({ label, amount, toneClass = 'text-gray-900' }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
    <p className="text-[13px] text-gray-500 mb-1">{label}</p>
    <p className={`text-[16px] font-medium ${toneClass}`}>
      {formatRupiah(amount, 'Rp 0', { space: false })}
    </p>
  </div>
);

/**
 * The daily charge ledger for one rental, from `GET /rental/:id/charges`.
 *
 * Answers what the arrears number cannot: which days are unpaid, what each
 * day was billed, and which payment settled it. Server-paginated and
 * server-filtered, like the payment history beside it.
 */
export const ChargeLedgerTab: React.FC<ChargeLedgerTabProps> = ({
  rentalId,
  waivers,
  statusFilter,
  onStatusFilterChange,
  collectionFilter,
  onClearCollectionFilter,
}) => {
  const [rows, setRows] = useState<RentalCharge[]>([]);
  const [summary, setSummary] =
    useState<RentalChargeSummary>(EMPTY_CHARGE_SUMMARY);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const columns = useMemo(() => buildColumns(waivers), [waivers]);

  const fetchCharges = useCallback(
    async (
      page: number,
      size: number,
      status: ChargeStatusFilter,
      collectionID?: string
    ) => {
      setIsLoading(true);
      try {
        const response = await getRentalCharges(rentalId, {
          page,
          size,
          ...(status === 'ALL' ? {} : { status: [status] }),
          ...(collectionID ? { collectionID } : {}),
        });

        setRows(response?.items || []);
        setSummary(response?.summary || EMPTY_CHARGE_SUMMARY);
        setTotal(response?.pagination?.total || 0);
        setErrorMessage(null);
      } catch (error) {
        // Inline, never a toast: the endpoint ships separately from this
        // screen, and a 404 on every page and filter change would stack a
        // wall of toasts over a rental ops still need to read.
        setErrorMessage(
          extractApiErrorMessage(error, 'Gagal memuat rincian tagihan')
        );
        setRows([]);
        setSummary(EMPTY_CHARGE_SUMMARY);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [rentalId]
  );

  useEffect(() => {
    fetchCharges(currentPage, pageSize, statusFilter, collectionFilter?.ref);
  }, [
    fetchCharges,
    currentPage,
    pageSize,
    statusFilter,
    collectionFilter?.ref,
  ]);

  // Halaman dikembalikan ke awal saat pembayaran yang dilihat berganti:
  // halaman 3 dari ledger penuh hampir pasti kosong untuk satu pembayaran.
  useEffect(() => {
    setCurrentPage(1);
  }, [collectionFilter?.ref]);

  // Judul dan isi dipilih bersama, supaya keduanya tidak bisa menjelaskan dua
  // keadaan berbeda. Kosong karena penyempitan pembayaran bukan data hilang:
  // pembayaran yang menambah saldo prepaid diselesaikan engine harian tanpa
  // menunjuk transaksi, dan 315 dari 5.706 pembayaran lunas di produksi
  // berbentuk begitu.
  const emptyState = collectionFilter
    ? {
        title: 'Pembayaran Ini Tidak Menutup Hari Tertentu',
        body: 'Pembayaran ini menambah saldo prepaid, dan hari-harinya diselesaikan engine harian tanpa menunjuk transaksi tertentu.',
      }
    : statusFilter !== 'ALL'
      ? {
          title: 'Tidak Ada Tagihan Dengan Status Ini',
          body: 'Coba pilih status lain untuk melihat hari tagihan lainnya.',
        }
      : {
          title: 'Belum Ada Rincian Tagihan',
          body: 'Tagihan harian akan muncul di sini setelah sewa berjalan.',
        };

  return (
    <div>
      {/* Whole-rental totals: they deliberately do NOT follow the table's
          status filter, so the header stays a stable reference while ops
          narrow the rows down. `totalPending` is the same arrears figure the
          Detail tab shows. */}
      <div className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCell label="Total Ditagih" amount={summary.totalCharged} />
        <SummaryCell
          label="Sudah Dibayar"
          amount={summary.totalPaid}
          toneClass="text-[#1FC16B]"
        />
        <SummaryCell
          label="Tertunggak"
          amount={summary.totalPending}
          toneClass={
            summary.totalPending > 0 ? 'text-[#FB3748]' : 'text-gray-900'
          }
        />
        <SummaryCell label="Digratiskan" amount={summary.totalWaived} />
      </div>

      {collectionFilter && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <p className="text-[14px] text-gray-700">
            Menampilkan hari yang dibayar{' '}
            <span className="font-medium text-gray-900">
              {collectionFilter.code}
            </span>
          </p>
          {onClearCollectionFilter && (
            <Button
              size="small"
              variant="neutral"
              onClick={onClearCollectionFilter}
            >
              Tampilkan semua
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_FILTERS.map((option) => (
          <Button
            key={option.key}
            variant={statusFilter === option.key ? 'primary' : 'white'}
            size="small"
            onClick={() => {
              onStatusFilterChange(option.key);
              setCurrentPage(1);
            }}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-gray-600">Memuat rincian tagihan...</span>
        </div>
      ) : errorMessage ? (
        <div className="bg-white rounded-lg border border-gray-200 py-12 px-6 text-center">
          <i className="ri-error-warning-line text-[28px] text-gray-400"></i>
          <p className="text-[14px] text-gray-700 mt-2 mb-4">{errorMessage}</p>
          <Button
            variant="white"
            size="small"
            onClick={() =>
              fetchCharges(
                currentPage,
                pageSize,
                statusFilter,
                collectionFilter?.ref
              )
            }
          >
            Coba Lagi
          </Button>
        </div>
      ) : (
        <TableData
          data={rows}
          columns={columns}
          size="small"
          className="min-w-full"
          showHeaderWhenEmpty
          emptyState={
            <div className="py-16">
              <div className="w-[148px] h-[148px] mx-auto mb-4">
                <img
                  src="/images/no-results.png"
                  alt="Belum ada tagihan"
                  className="w-full h-full"
                />
              </div>
              <h3
                className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                style={{ letterSpacing: '-1.5%' }}
              >
                {emptyState.title}
              </h3>
              <p
                className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center max-w-[400px] mx-auto"
                style={{ letterSpacing: '-0.6%' }}
              >
                {emptyState.body}
              </p>
            </div>
          }
        />
      )}

      {!errorMessage && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
};

export default ChargeLedgerTab;
